import React from 'react';
import type { CaseAward } from '@lnr-v2/types';
import { useLanguage } from '../../../contexts/LanguageContext';
import { resolveCaseAwards } from '../../../constants/awards';
import styles from './AwardsSection.module.css';

interface AwardsSectionProps {
  awards?: CaseAward[];
  className?: string;
}

export const AwardsSection: React.FC<AwardsSectionProps> = ({
  awards,
  className,
}) => {
  const { language, t } = useLanguage();
  const lang = language === 'fr' ? 'fr' : 'en';
  const resolvedAwards = resolveCaseAwards(awards, lang, t);
  if (resolvedAwards.length === 0) {
    return null;
  }

  const visibleAwards = resolvedAwards.slice(0, 3);
  const remainingCount = Math.max(resolvedAwards.length - 3, 0);

  const overflowKey = 'cases.awards.more';
  const fallbackOverflowLabel =
    lang === 'fr'
      ? `+ ${remainingCount} prix creatives`
      : `+ ${remainingCount} creative awards`;
  const translatedOverflow = t(overflowKey, { count: String(remainingCount) });
  const overflowLabel =
    translatedOverflow !== overflowKey
      ? translatedOverflow.replace('{{count}}', String(remainingCount))
      : fallbackOverflowLabel;

  return (
    <section className={`${styles.awardsSection} ${className || ''}`}>
      <div className={styles.grid}>
        {visibleAwards.map((award) => (
          <article
            key={`${award.key}-${award.year || 'na'}`}
            className={styles.card}
          >
            <div className={styles.brandRowLogoOnly}>
              {award.resolvedImageUrl ? (
                <img
                  src={award.resolvedImageUrl}
                  alt={award.displayLabel}
                  className={styles.logo}
                  loading="lazy"
                />
              ) : (
                <h3 className={styles.awardLabel}>{award.displayLabel}</h3>
              )}
            </div>
          </article>
        ))}
        {remainingCount > 0 && (
          <article className={`${styles.card} ${styles.moreCard}`}>
            <p className={styles.moreLabel}>{overflowLabel}</p>
          </article>
        )}
      </div>
    </section>
  );
};
